import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, Autocomplete, Checkbox, InputAdornment, FormControlLabel, Switch } from '@mui/material';
import ConfirmDialog from '../../reuseable-components/ConfirmDialog/ConfirmDialog';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import AutocompleteWithSelectAll from '../../reuseable-components/Autocomplete/AutocompleteWithSelectAll';
import { excelFrontendDownload } from '../../utils/excelFrontendDownload'

OrderGuideUpdateByPercenatge.propTypes = {
  selectedOrderGuide: PropTypes.object,
  products: PropTypes.array,
  purchasingGroups: PropTypes.array,
  productCategories: PropTypes.array,
  facilities: PropTypes.array,
  loadingProducts: PropTypes.bool,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function OrderGuideUpdateByPercenatge({ selectedOrderGuide, products, purchasingGroups, productCategories, facilities, open, close }) {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [formValue, setFormValue] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [productsBelowVendorPrice, setProductsBelowVendorPrice] = useState({});
  const [error, setError] = useState(null);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    axios.get("/vendor/vendors")
      .then(res => { setVendors(res.data.vendors.map(v => ({ label: v.name, value: v.id }))); })
      .catch(() => enqueueSnackbar('Get vendors data failed', { variant: 'error' }))
    if (selectedOrderGuide) {
      const isUsingPurchasingGroup = selectedOrderGuide.purchasingGroupId > 0;
      setFormValue({
        usePurchasingGroup: isUsingPurchasingGroup,
        purchasingGroupIds: isUsingPurchasingGroup ? [selectedOrderGuide.purchasingGroupId] : [],
        facilityIds: !isUsingPurchasingGroup ? [selectedOrderGuide.facilityId] : [],
        productCategoryIds: [selectedOrderGuide.productCategoryId],
        isOverwrite: true,
      });
    }
    else { setFormValue({ usePurchasingGroup: true, isOverwrite: true }); }
  }, [open])

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/orderGuide/pricingUpdate", objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          const results = res.data.map(item => { const { purchasingGroupId, facilityId, ...rest } = item;
            return { ...rest,
              purchasingGroupName: purchasingGroups.find(pg => pg.value === purchasingGroupId)?.label || "",
              facilityName: facilities.find(f => f.value === facilityId)?.label || ""
            };
          });   
          if (results.length > 0) {
            const originalHeaders = Object.keys(results[0]);
            const headerMap = {
              productId: "Product ID", uomType: "UOM Type", newPrice: "Updated Price", oldPrice: "Old Price",
              purchasingGroupName: "Purchasing Group", facilityName: "Facility"
            };
            const headers = originalHeaders.map(h => headerMap[h] || h);
            const data = results.map(row => originalHeaders.map(h => row[h]));
            const date = new Date().toISOString().split("T")[0];
            const fileName = `Order Guide Products Result - ${date}.xlsx`;
            const formats = { "Updated Price": "$#,##0.00", "Old Price": "$#,##0.00"};
            excelFrontendDownload({ fields: headers, data, fileName, worksheetName: "Order-Guide-Products", formats });
          }
          enqueueSnackbar(`Order guides percentage update succeeded`, { variant: 'success' });
          close();      
        }
        else enqueueSnackbar(`Order guide percentage update failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`Order guide percentage update failed`, { variant: 'error' }))
      .finally(() => { setSubmitting(false); setShowConfirmation(false) })
  }

  const handelSetConfirmation = () => {
    const percentage = parseFloat(formValue.percentage ?? 0);
    if (percentage < 0) {
      axios.post("/orderGuide/productsForPercentageWarning", objectToFormData(formValue))
        .then(res => {
          if (res.data.error != null) setError(res.data.error);
          else {
            const facilityMap = formValue.usePurchasingGroup ? Object.fromEntries(purchasingGroups.map(pg => [pg.value, pg.label]))
              : Object.fromEntries(facilities.map(f => [f.value, f.label]));
            const ProductsUOMMap = Object.fromEntries(products.map(p => [p.id, p.vendorItemId]))
            const warnings = (res.data.warningProducts ?? []).map(x => {
              const name = facilityMap[x.facility]
              const SKU = ProductsUOMMap[x.productUOMId]
              return { ...x, facilityName: name, vendorItemId: SKU };
            });
            setProductsBelowVendorPrice(warnings)
          }
        })
        .catch(() => enqueueSnackbar('Get order guide products data failed', { variant: 'error' }))
        .finally(() => setShowConfirmation(true));
    }
    else setShowConfirmation(true)
  }

  return <Dialog open={open} onClose={close} maxWidth="md" >
    <DialogContent>
      <Stack spacing={2} minWidth="600px">
        <Typography variant="subtitle1" sx={{ textAlign: "center" }}>Update Pricing by Percentage</Typography>
        {formValue.usePurchasingGroup && <Box>
          <Typography variant="subtitle1">Purchasing Group*</Typography>
          <AutocompleteWithSelectAll
            fullWidth
            size="small"
            value={formValue.purchasingGroupIds ?? []}
            onChange={(vals) => setFormValue(f => ({ ...f, purchasingGroupIds: vals }))}
            options={purchasingGroups}
            placeholder={(formValue.purchasingGroupIds?.length ?? 0) > 0 ? "" : "Select Purchasing Group"}
            enableSelectAll
          />
        </Box>}
        {!formValue.usePurchasingGroup && <Box>
          <Typography variant="subtitle1">Facility*</Typography>
          <AutocompleteWithSelectAll
            fullWidth
            size="small"
            value={formValue.facilityIds ?? []}
            onChange={(vals) => setFormValue(f => ({ ...f, facilityIds: vals }))}
            options={facilities}
            placeholder={(formValue.facilityIds?.length ?? 0) > 0 ? "" : "Select Facility"}
            enableSelectAll
          />
        </Box>}
        <Box>
          <Typography variant="subtitle1">Vendor</Typography>
          <AutocompleteWithSelectAll
            fullWidth
            size="small"
            value={formValue.VendorIds ?? []}
            onChange={(vals) => setFormValue(f => ({ ...f, VendorIds: vals }))}
            options={vendors}
            placeholder={(formValue.VendorIds?.length ?? 0) > 0 ? "" : "Select Vendor"}
            enableSelectAll
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Product Category*</Typography>
          <AutocompleteWithSelectAll
            fullWidth
            size="small"
            value={formValue.productCategoryIds ?? []}
            onChange={(vals) => setFormValue(f => ({ ...f, productCategoryIds: vals }))}
            options={productCategories}
            placeholder={(formValue.productCategoryIds?.length ?? 0) > 0 ? "" : "Select Product Category"}
            enableSelectAll
          />
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ mr: 1 }}>Percentage*</Typography>
            <FormControlLabel
              label={formValue.isOverwrite ? "Overwrite" : "Add-On"}
              control={
                <Switch size="small" checked={formValue.isOverwrite === true}
                  onChange={(e) => setFormValue((f) => ({ ...f, isOverwrite: e.target.checked }))}
                  sx={{
                    "& .MuiSwitch-switchBase": { color: "primary.main", },
                    "& .MuiSwitch-track": { backgroundColor: "grey.400", opacity: 1 },
                  }}
                />
              }
            />
          </Box>

          <TextField
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, mt: "3px", width: 100, display: "block", }}
            value={formValue.percentage ?? ''} decimalScale={2}
            InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
            onChange={e => {
              let v = e.target.value;
              if (/^-?\d{0,3}(\.\d{0,2})?$/.test(v)) {
                if (v !== "-" && !v.endsWith(".")) v = v.replace(/^(-?)0+(?=\d)/, "$1");
                setFormValue(f => ({ ...f, percentage: v }));
              }
            }}
          />
          {formValue.percentage < 0 && <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}> Negative value will decrease the order guides pricing </Typography>}
        </Box>
        <Box>
          <Checkbox checked={formValue.usePurchasingGroup ?? false} onChange={(e) => setFormValue(value => ({ ...value, usePurchasingGroup: e.target.checked }))} />
          Order guide by purchasing group
        </Box>
        <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained"
            disabled={(formValue.usePurchasingGroup ? !formValue.purchasingGroupIds?.length > 0 : !formValue.facilityIds?.length > 0) ||
              !formValue.productCategoryIds?.length > 0 || !(formValue.percentage > 0 || formValue.percentage < 0)}
            onClick={() => { handelSetConfirmation(); }}>
            Save
          </LoadingButton>
        </Box>
        {showConfirmation && (<ConfirmDialog
          action={() => onSubmit()}
          cancelAction={() => { setShowConfirmation(false); setProductsBelowVendorPrice({}); setError(null); }}
          dialogOnly
          actionButton="Save"
          color="primary"
          title={<Typography variant="h6" fontWeight={600} textAlign="center"> The following order guides will be updated by {formValue.percentage}%. </Typography>}
          content={
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {formValue.usePurchasingGroup ? (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}> Purchasing Groups: </Typography>
                  <Typography variant="body1" color="text.secondary"> {purchasingGroups.filter(pg => formValue.purchasingGroupIds?.includes(pg.value))
                    .map(pg => pg.label).join(", ")} </Typography>
                </Box>
              ) : (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}> Facilities: </Typography>
                  <Typography variant="body1" color="text.secondary">{facilities.filter(fac => formValue.facilityIds?.includes(fac.value))
                    .map(fac => fac.label).join(", ")} </Typography>
                </Box>)}
              <Box>
                <Typography variant="subtitle1" fontWeight={600}> Product Categories: </Typography>
                <Typography variant="body1" color="text.secondary">{productCategories.filter(pc => formValue.productCategoryIds?.includes(pc.value))
                  .map(pc => pc.label).join(", ")}</Typography>
              </Box>
              {productsBelowVendorPrice.length > 0 && (
                <Box sx={{ mt: 2, p: 2, border: "1px solid red", borderRadius: 1, backgroundColor: "#ffe6e6" }}>
                  <Typography variant="subtitle1" fontWeight={600} color="error">
                    Warning: The following products will have prices below vendor cost!
                  </Typography>
                  {productsBelowVendorPrice.map(product => (
                    <Typography key={product.id} variant="body2" color="error" sx={{ mb: 2 }}>
                      Facility / Group: {product.facilityName} | SKU: {product.vendorItemId} | Vendor Price: ${product.vendorPrice.toFixed(2)} | New Price: ${product.newPrice.toFixed(2)}
                    </Typography>
                  ))}
                </Box>
              )}
              {error != null && (<Typography sx={{ mt: 2 }} variant="subtitle1" fontWeight={600} color="error"> {error}, verify update! </Typography>)}
            </Box>} />
        )}
      </Stack>
    </DialogContent>
  </Dialog>;
}

